import React, { useState, useMemo } from 'react'
import { router, usePage } from '@inertiajs/react'
import TextField from '@mui/material/TextField'
import Button from '@mui/material/Button'
import Box from '@mui/material/Box'
import Grid from '@mui/material/Grid'
import Paper from '@mui/material/Paper'
import Typography from '@mui/material/Typography'
import FormControl from '@mui/material/FormControl'
import InputLabel from '@mui/material/InputLabel'
import Select from '@mui/material/Select'
import MenuItem from '@mui/material/MenuItem'
import FormControlLabel from '@mui/material/FormControlLabel'
import Checkbox from '@mui/material/Checkbox'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import Chip from '@mui/material/Chip'
import Alert from '@mui/material/Alert'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import EditIcon from '@mui/icons-material/Edit'
import IconButton from '@mui/material/IconButton'
import Tooltip from '@mui/material/Tooltip'
import Tabs from '@mui/material/Tabs'
import Tab from '@mui/material/Tab'
import Dialog from '@mui/material/Dialog'
import DialogTitle from '@mui/material/DialogTitle'
import DialogContent from '@mui/material/DialogContent'
import DialogActions from '@mui/material/DialogActions'
import Accordion from '@mui/material/Accordion'
import AccordionSummary from '@mui/material/AccordionSummary'
import AccordionDetails from '@mui/material/AccordionDetails'
import InputAdornment from '@mui/material/InputAdornment'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import PersonIcon from '@mui/icons-material/Person'
import DescriptionIcon from '@mui/icons-material/Description'
import MemoryIcon from '@mui/icons-material/Psychology'
import PhoneCallbackIcon from '@mui/icons-material/PhoneCallback'
import ExtensionIcon from '@mui/icons-material/Extension'
import VpnKeyIcon from '@mui/icons-material/VpnKey'
import CableIcon from '@mui/icons-material/Cable'
import DeleteIcon from '@mui/icons-material/Delete'
import SearchIcon from '@mui/icons-material/Search'
import SmartToyIcon from '@mui/icons-material/SmartToy'
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings'
import { SidebarLayout } from '@/components/layouts/SidebarLayout'
import { ConfirmDialog } from '@/components/shared/ConfirmDialog'
import { Markdown } from '@/components/Markdown'

interface AgentData {
  id: number
  username: string
  agent_type: 'human' | 'machine'
  admin: boolean
  slug: string
  email: string | null
  profile: string | null
  agent_provider: string | null
  agent_arguments: string | null
  api_doc_skill_slugs: string | null
  memory: string | null
  calling: string | null
  calling_interval_minutes: number | null
  last_calling_at: string | null
  created_at: string
  updated_at: string
}

interface ApiDocSkillEntry {
  slug: string
  name: string
  description: string
}

interface ConnectionData {
  id: number
  name: string
  status: 'pending' | 'approved' | 'denied' | 'revoked'
  metadata: Record<string, string>
  last_used_at: string | null
  approved_at: string | null
  approved_by: string | null
  revoked_at: string | null
  revoked_by: string | null
  created_at: string
}

interface MemoryData {
  id: number
  subject_type: string
  subject_id: number | null
  content: string
  created_at: string
  updated_at: string
}

interface Props {
  agent: AgentData
  connections: ConnectionData[]
  memories: MemoryData[]
  available_api_doc_skills: ApiDocSkillEntry[]
  registration_url: string | null
  installer_url: string | null
  registration_token_expires_at: string | null
  minimum_password_length: number
}

const statusColors: Record<string, 'warning' | 'success' | 'error' | 'default'> = {
  pending: 'warning',
  approved: 'success',
  denied: 'error',
  revoked: 'default',
}

const STATUS_LABELS: Record<string, string> = {
  pending: 'Pending',
  approved: 'Approved',
  denied: 'Denied',
  revoked: 'Revoked',
}

const PROVIDER_LABELS: Record<string, string> = {
  CLAUDE_CODE: 'Claude Code',
}

const TAB_SLUGS = ['settings', 'memories'] as const

function SectionHeader({ icon, title, action }: { icon: React.ReactNode; title: string; action?: React.ReactNode }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Box sx={{ color: 'text.secondary', display: 'flex' }}>{icon}</Box>
        <Typography variant="subtitle1" fontWeight={600}>{title}</Typography>
      </Box>
      {action}
    </Box>
  )
}

function InfoRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <Box sx={{ display: 'flex', py: 1.5, borderBottom: '1px solid', borderColor: 'divider', '&:last-child': { borderBottom: 'none' } }}>
      <Typography variant="body2" color="text.secondary" sx={{ width: 160, flexShrink: 0, fontWeight: 500, pt: 0.25 }}>
        {label}
      </Typography>
      <Box sx={{ flex: 1, minWidth: 0 }}>{children}</Box>
    </Box>
  )
}

// --- Edit Dialogs ---

function EditFieldDialog({
  open,
  title,
  fieldLabel,
  value,
  onClose,
  onSave,
  multiline,
  helperText,
  monospace,
  error,
}: {
  open: boolean
  title: string
  fieldLabel: string
  value: string
  onClose: () => void
  onSave: (value: string) => void
  multiline?: boolean
  helperText?: string
  monospace?: boolean
  error?: string
}) {
  const [fieldValue, setFieldValue] = useState(value)

  // Reset when opening
  React.useEffect(() => {
    if (open) setFieldValue(value)
  }, [open, value])

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <TextField
          label={fieldLabel}
          fullWidth
          autoFocus
          multiline={multiline}
          minRows={multiline ? 4 : undefined}
          value={fieldValue}
          onChange={e => setFieldValue(e.target.value)}
          error={!!error}
          helperText={error || helperText}
          slotProps={monospace ? { input: { sx: { fontFamily: 'monospace', fontSize: '0.85rem' } } } : undefined}
          sx={{ mt: 1 }}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={() => onSave(fieldValue)}>Save</Button>
      </DialogActions>
    </Dialog>
  )
}

function EditSelectDialog({
  open,
  title,
  fieldLabel,
  value,
  options,
  onClose,
  onSave,
}: {
  open: boolean
  title: string
  fieldLabel: string
  value: string
  options: { value: string; label: string }[]
  onClose: () => void
  onSave: (value: string) => void
}) {
  const [fieldValue, setFieldValue] = useState(value)

  React.useEffect(() => {
    if (open) setFieldValue(value)
  }, [open, value])

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <FormControl fullWidth sx={{ mt: 1 }}>
          <InputLabel>{fieldLabel}</InputLabel>
          <Select value={fieldValue} label={fieldLabel} onChange={e => setFieldValue(e.target.value)}>
            {options.map(opt => (
              <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
            ))}
          </Select>
        </FormControl>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={() => onSave(fieldValue)}>Save</Button>
      </DialogActions>
    </Dialog>
  )
}

function EditCheckboxDialog({
  open,
  title,
  fieldLabel,
  checked,
  onClose,
  onSave,
}: {
  open: boolean
  title: string
  fieldLabel: string
  checked: boolean
  onClose: () => void
  onSave: (checked: boolean) => void
}) {
  const [fieldValue, setFieldValue] = useState(checked)

  React.useEffect(() => {
    if (open) setFieldValue(checked)
  }, [open, checked])

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <FormControlLabel
          control={<Checkbox checked={fieldValue} onChange={e => setFieldValue(e.target.checked)} />}
          label={fieldLabel}
          sx={{ mt: 1 }}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={() => onSave(fieldValue)}>Save</Button>
      </DialogActions>
    </Dialog>
  )
}

function EditPasswordDialog({
  open,
  onClose,
  onSave,
  minimumLength,
}: {
  open: boolean
  onClose: () => void
  onSave: (password: string, confirmation: string) => void
  minimumLength: number
}) {
  const [password, setPassword] = useState('')
  const [confirmation, setConfirmation] = useState('')

  React.useEffect(() => {
    if (open) {
      setPassword('')
      setConfirmation('')
    }
  }, [open])

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Change password</DialogTitle>
      <DialogContent>
        <TextField
          label="New password"
          type="password"
          fullWidth
          autoFocus
          autoComplete="new-password"
          value={password}
          onChange={e => setPassword(e.target.value)}
          helperText={`Minimum ${minimumLength} characters`}
          sx={{ mt: 1, mb: 2 }}
        />
        <TextField
          label="Confirm new password"
          type="password"
          fullWidth
          autoComplete="new-password"
          value={confirmation}
          onChange={e => setConfirmation(e.target.value)}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={() => onSave(password, confirmation)}>Save</Button>
      </DialogActions>
    </Dialog>
  )
}

// --- API Doc Skills Section (compact chip-based) ---

function ApiDocSkillsSection({
  agent,
  available_api_doc_skills,
  submitUpdate,
}: {
  agent: AgentData
  available_api_doc_skills: ApiDocSkillEntry[]
  submitUpdate: (data: Record<string, any>) => void
}) {
  const currentSlugs: string[] | null = agent.api_doc_skill_slugs ? JSON.parse(agent.api_doc_skill_slugs) : null
  const allEnabled = currentSlugs === null
  const enabledSet = new Set(currentSlugs ?? available_api_doc_skills.map(s => s.slug))

  function handleToggleSkill(slug: string) {
    const base = currentSlugs ?? available_api_doc_skills.map(s => s.slug)
    const isEnabled = enabledSet.has(slug)
    const updated = isEnabled ? base.filter(s => s !== slug) : [...base, slug]
    submitUpdate({ api_doc_skill_slugs: JSON.stringify(updated) })
  }

  function handleSelectAll() {
    submitUpdate({ api_doc_skill_slugs: JSON.stringify(available_api_doc_skills.map(s => s.slug)) })
  }

  function handleDeselectAll() {
    submitUpdate({ api_doc_skill_slugs: JSON.stringify([]) })
  }

  function handleReset() {
    submitUpdate({ api_doc_skill_slugs: '' })
  }

  return (
    <Paper elevation={0} sx={{ p: 3, border: '1px solid', borderColor: 'divider', borderRadius: 2, mb: 3 }}>
      <SectionHeader
        icon={<ExtensionIcon fontSize="small" />}
        title="API doc skills"
        action={
          <Box sx={{ display: 'flex', gap: 0.5 }}>
            <Button size="small" onClick={handleSelectAll}>All</Button>
            <Button size="small" onClick={handleDeselectAll}>None</Button>
            {!allEnabled && (
              <Button size="small" color="secondary" onClick={handleReset}>Reset</Button>
            )}
          </Box>
        }
      />
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
        {allEnabled
          ? 'All skills enabled (default). Click a skill to disable it.'
          : `${enabledSet.size} of ${available_api_doc_skills.length} skills enabled.`
        }
      </Typography>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
        {available_api_doc_skills.map(skill => (
          <Tooltip key={skill.slug} title={skill.description} arrow>
            <Chip
              label={skill.name}
              size="small"
              variant={enabledSet.has(skill.slug) ? 'filled' : 'outlined'}
              color={enabledSet.has(skill.slug) ? 'primary' : 'default'}
              onClick={() => handleToggleSkill(skill.slug)}
              sx={{
                fontFamily: 'monospace',
                fontSize: '0.8rem',
                cursor: 'pointer',
                opacity: enabledSet.has(skill.slug) ? 1 : 0.6,
                '&:hover': { opacity: 1 },
              }}
            />
          </Tooltip>
        ))}
      </Box>
    </Paper>
  )
}

// --- Settings Tab ---

function SettingsTab({
  agent,
  connections,
  available_api_doc_skills,
  registration_url,
  installer_url,
  registration_token_expires_at,
  minimum_password_length,
}: {
  agent: AgentData
  connections: ConnectionData[]
  available_api_doc_skills: ApiDocSkillEntry[]
  registration_url: string | null
  installer_url: string | null
  registration_token_expires_at: string | null
  minimum_password_length: number
}) {
  const { current_agent } = usePage().props
  const isSelf = (current_agent as any)?.id === agent.id
  const [copiedField, setCopiedField] = useState<string | null>(null)
  const [revokeConnectionId, setRevokeConnectionId] = useState<number | null>(null)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)

  // Edit dialog states
  const [editField, setEditField] = useState<string | null>(null)

  function submitUpdate(data: Record<string, any>) {
    router.put(`/manage/agents/${agent.slug}`, { agent: data }, { preserveScroll: true })
    setEditField(null)
  }

  function handleCopy(text: string, field: string) {
    navigator.clipboard.writeText(text)
    setCopiedField(field)
    setTimeout(() => setCopiedField(null), 2000)
  }

  function handleGenerateToken() {
    router.post(`/manage/agents/${agent.slug}/generate_registration_token`, {}, { preserveScroll: true })
  }

  function handleApproveConnection(id: number) {
    router.post(`/manage/agents/connection_requests/${id}/approve`, {}, { preserveScroll: true })
  }

  function handleDenyConnection(id: number) {
    router.post(`/manage/agents/connection_requests/${id}/deny`, {}, { preserveScroll: true })
  }

  function handleRevokeConnection() {
    if (revokeConnectionId) {
      router.post(`/manage/agents/connection_requests/${revokeConnectionId}/revoke`, {}, { preserveScroll: true })
      setRevokeConnectionId(null)
    }
  }

  function handleDelete() {
    router.delete(`/manage/agents/${agent.slug}`)
    setShowDeleteConfirm(false)
  }

  function EditButton({ field }: { field: string }) {
    return (
      <Tooltip title="Edit">
        <IconButton size="small" onClick={() => setEditField(field)} sx={{ ml: 0.5 }}>
          <EditIcon sx={{ fontSize: 16 }} />
        </IconButton>
      </Tooltip>
    )
  }

  const isMachine = agent.agent_type === 'machine'

  // Left column content (primary sections)
  const leftColumn = (
    <>
      {/* Agent Information */}
      <Paper elevation={0} sx={{ p: 3, border: '1px solid', borderColor: 'divider', borderRadius: 2, mb: 3 }}>
        <SectionHeader icon={<PersonIcon fontSize="small" />} title="Agent information" />
        <Box>
          <InfoRow label="Username">
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Typography variant="body2">{agent.username}</Typography>
              <EditButton field="username" />
            </Box>
          </InfoRow>
          <InfoRow label="Type">
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Chip label={agent.agent_type === 'human' ? 'Human' : 'Machine'} size="small" color={isMachine ? 'info' : 'default'} />
              <EditButton field="agent_type" />
            </Box>
          </InfoRow>
          <InfoRow label="Admin">
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Chip label={agent.admin ? 'Yes' : 'No'} size="small" color={agent.admin ? 'success' : 'default'} />
              <EditButton field="admin" />
            </Box>
          </InfoRow>
          {agent.agent_type === 'human' && (
            <InfoRow label="Email">
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Typography variant="body2">{agent.email || <Typography component="span" variant="body2" color="text.secondary">Not set</Typography>}</Typography>
                <EditButton field="email" />
              </Box>
            </InfoRow>
          )}
          {agent.agent_type === 'human' && (
            <InfoRow label="Password">
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Typography variant="body2" color="text.secondary">********</Typography>
                <EditButton field="password" />
              </Box>
            </InfoRow>
          )}
          {isMachine && (
            <>
              <InfoRow label="Provider">
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Typography variant="body2">{PROVIDER_LABELS[agent.agent_provider || ''] || agent.agent_provider || '-'}</Typography>
                  <EditButton field="agent_provider" />
                </Box>
              </InfoRow>
              <InfoRow label="Arguments">
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>
                    {agent.agent_arguments || '-'}
                  </Typography>
                  <EditButton field="agent_arguments" />
                </Box>
              </InfoRow>
            </>
          )}
          <InfoRow label="Created">
            <Typography variant="body2" color="text.secondary">{new Date(agent.created_at).toLocaleString()}</Typography>
          </InfoRow>
          <InfoRow label="Updated">
            <Typography variant="body2" color="text.secondary">{new Date(agent.updated_at).toLocaleString()}</Typography>
          </InfoRow>
        </Box>
      </Paper>

      {/* Profile section for machine agents */}
      {isMachine && (
        <Paper elevation={0} sx={{ p: 3, border: '1px solid', borderColor: 'divider', borderRadius: 2, mb: 3 }}>
          <SectionHeader
            icon={<DescriptionIcon fontSize="small" />}
            title="Profile"
            action={<Button size="small" startIcon={<EditIcon />} onClick={() => setEditField('profile')}>Edit</Button>}
          />
          {agent.profile ? (
            <Box sx={{ maxHeight: 400, overflow: 'auto', '& p:first-of-type': { mt: 0 }, '& p:last-of-type': { mb: 0 } }}>
              <Markdown>{agent.profile}</Markdown>
            </Box>
          ) : (
            <Typography variant="body2" color="text.secondary">No profile set.</Typography>
          )}
        </Paper>
      )}

      {/* Memory section for machine agents */}
      {isMachine && (
        <Paper elevation={0} sx={{ p: 3, border: '1px solid', borderColor: 'divider', borderRadius: 2, mb: 3 }}>
          <SectionHeader
            icon={<MemoryIcon fontSize="small" />}
            title="Persistent memory"
            action={<Button size="small" startIcon={<EditIcon />} onClick={() => setEditField('memory')}>Edit</Button>}
          />
          {agent.memory ? (
            <Box sx={{ maxHeight: 300, overflow: 'auto', '& p:first-of-type': { mt: 0 }, '& p:last-of-type': { mb: 0 } }}>
              <Markdown>{agent.memory}</Markdown>
            </Box>
          ) : (
            <Typography variant="body2" color="text.secondary">No memory stored. Synced automatically by the agent runner.</Typography>
          )}
        </Paper>
      )}

      {/* Calling section for machine agents */}
      {isMachine && (
        <Paper elevation={0} sx={{ p: 3, border: '1px solid', borderColor: 'divider', borderRadius: 2, mb: 3 }}>
          <SectionHeader
            icon={<PhoneCallbackIcon fontSize="small" />}
            title="Calling"
            action={<Button size="small" startIcon={<EditIcon />} onClick={() => setEditField('calling')}>Edit</Button>}
          />
          {agent.calling ? (
            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', fontFamily: 'monospace', fontSize: '0.85rem', maxHeight: 200, overflow: 'auto' }}>
              {agent.calling}
            </Typography>
          ) : (
            <Typography variant="body2" color="text.secondary">No calling set. When configured, the agent will proactively act on this purpose when idle.</Typography>
          )}
          <Box sx={{ mt: 2 }}>
            <InfoRow label="Interval">
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Typography variant="body2">{agent.calling_interval_minutes ?? 60} minutes</Typography>
                <EditButton field="calling_interval_minutes" />
              </Box>
            </InfoRow>
            {agent.last_calling_at && (
              <InfoRow label="Last acted">
                <Typography variant="body2" color="text.secondary">{new Date(agent.last_calling_at).toLocaleString()}</Typography>
              </InfoRow>
            )}
          </Box>
        </Paper>
      )}
    </>
  )

  // Right column content (secondary sections) — only for machine agents
  const rightColumn = isMachine ? (
    <>
      {/* Quick Info Card */}
      <Paper elevation={0} sx={{ p: 3, border: '1px solid', borderColor: 'divider', borderRadius: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
          <SmartToyIcon sx={{ color: 'primary.main', fontSize: 32 }} />
          <Box>
            <Typography variant="h6" fontWeight={700} sx={{ lineHeight: 1.2 }}>@{agent.username}</Typography>
            <Typography variant="caption" color="text.secondary">Machine Agent</Typography>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mb: 1.5 }}>
          <Chip label={PROVIDER_LABELS[agent.agent_provider || ''] || agent.agent_provider || 'No provider'} size="small" color="primary" variant="outlined" />
          {agent.admin && <Chip icon={<AdminPanelSettingsIcon />} label="Admin" size="small" color="success" variant="outlined" />}
          {agent.calling && <Chip label="Calling active" size="small" color="info" variant="outlined" />}
        </Box>
        <Typography variant="caption" color="text.secondary">
          Created {new Date(agent.created_at).toLocaleDateString()}
        </Typography>
      </Paper>

      {/* API Doc Skills */}
      <ApiDocSkillsSection agent={agent} available_api_doc_skills={available_api_doc_skills} submitUpdate={submitUpdate} />

      {/* Registration */}
      <Paper elevation={0} sx={{ p: 3, border: '1px solid', borderColor: 'divider', borderRadius: 2, mb: 3 }}>
        <SectionHeader icon={<VpnKeyIcon fontSize="small" />} title="Registration" />
        {registration_url ? (
          <Box>
            <Alert severity="info" sx={{ mb: 2 }}>
              Token active. Expires {new Date(registration_token_expires_at!).toLocaleString()}.
            </Alert>
            {installer_url && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" fontWeight={500} sx={{ mb: 0.5 }}>Installer script</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <TextField
                    fullWidth
                    size="small"
                    value={`curl -fsSL ${installer_url} | bash`}
                    slotProps={{ input: { readOnly: true, sx: { fontFamily: 'monospace', fontSize: '0.8rem' } } }}
                  />
                  <Tooltip title={copiedField === 'installer' ? 'Copied!' : 'Copy'}>
                    <IconButton onClick={() => handleCopy(`curl -fsSL ${installer_url} | bash`, 'installer')} size="small">
                      <ContentCopyIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Box>
            )}
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" fontWeight={500} sx={{ mb: 0.5 }}>Manual registration</Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <TextField
                  fullWidth
                  size="small"
                  value={`okonomi-agent register ${registration_url}`}
                  slotProps={{ input: { readOnly: true, sx: { fontFamily: 'monospace', fontSize: '0.8rem' } } }}
                />
                <Tooltip title={copiedField === 'register' ? 'Copied!' : 'Copy'}>
                  <IconButton onClick={() => handleCopy(`okonomi-agent register ${registration_url}`, 'register')} size="small">
                    <ContentCopyIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>
            <Button variant="outlined" size="small" onClick={handleGenerateToken}>
              Regenerate token
            </Button>
          </Box>
        ) : (
          <Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Generate a registration URL for this agent to connect from a remote machine.
            </Typography>
            <Button variant="outlined" onClick={handleGenerateToken}>
              Generate registration URL
            </Button>
          </Box>
        )}
      </Paper>

      {/* Connections */}
      <Paper elevation={0} sx={{ p: 3, border: '1px solid', borderColor: 'divider', borderRadius: 2, mb: 3 }}>
        <SectionHeader icon={<CableIcon fontSize="small" />} title="Connections" />
        {connections.length > 0 ? (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {connections.map((conn) => (
                  <TableRow key={conn.id}>
                    <TableCell>
                      <Typography variant="body2" fontWeight={500}>{conn.name}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {conn.last_used_at ? `Used ${new Date(conn.last_used_at).toLocaleDateString()}` : `Created ${new Date(conn.created_at).toLocaleDateString()}`}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip label={STATUS_LABELS[conn.status] || conn.status} size="small" color={statusColors[conn.status] || 'default'} />
                    </TableCell>
                    <TableCell align="right">
                      {conn.status === 'pending' && (
                        <>
                          <Button size="small" color="success" onClick={() => handleApproveConnection(conn.id)} sx={{ mr: 0.5 }}>
                            Approve
                          </Button>
                          <Button size="small" color="error" onClick={() => handleDenyConnection(conn.id)}>
                            Deny
                          </Button>
                        </>
                      )}
                      {conn.status === 'approved' && (
                        <Button size="small" color="error" onClick={() => setRevokeConnectionId(conn.id)}>
                          Revoke
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        ) : (
          <Typography variant="body2" color="text.secondary">
            No connections yet. Generate a registration URL and run it on a remote machine.
          </Typography>
        )}
      </Paper>

      {/* Danger zone */}
      {!isSelf && (
        <Paper elevation={0} sx={{ p: 3, border: '1px solid', borderColor: 'error.main', borderRadius: 2 }}>
          <SectionHeader icon={<DeleteIcon fontSize="small" color="error" />} title="Danger zone" />
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Permanently delete this agent and all associated data.
          </Typography>
          <Button variant="outlined" color="error" onClick={() => setShowDeleteConfirm(true)}>
            Delete this agent
          </Button>
        </Paper>
      )}
    </>
  ) : null

  return (
    <>
      {isMachine ? (
        <Grid container spacing={3}>
          <Grid size={{ xs: 12, lg: 7 }}>
            {leftColumn}
          </Grid>
          <Grid size={{ xs: 12, lg: 5 }}>
            {rightColumn}
          </Grid>
        </Grid>
      ) : (
        <Box sx={{ maxWidth: 800 }}>
          {leftColumn}
          {/* Danger zone for human agents */}
          {!isSelf && (
            <Paper elevation={0} sx={{ p: 3, border: '1px solid', borderColor: 'error.main', borderRadius: 2 }}>
              <SectionHeader icon={<DeleteIcon fontSize="small" color="error" />} title="Danger zone" />
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Permanently delete this agent and all associated data.
              </Typography>
              <Button variant="outlined" color="error" onClick={() => setShowDeleteConfirm(true)}>
                Delete this agent
              </Button>
            </Paper>
          )}
        </Box>
      )}

      {/* Edit Dialogs */}
      <EditFieldDialog
        open={editField === 'username'}
        title="Edit username"
        fieldLabel="Username"
        value={agent.username}
        onClose={() => setEditField(null)}
        onSave={v => submitUpdate({ username: v })}
      />
      <EditSelectDialog
        open={editField === 'agent_type'}
        title="Edit agent type"
        fieldLabel="Agent type"
        value={agent.agent_type}
        options={[{ value: 'human', label: 'Human' }, { value: 'machine', label: 'Machine' }]}
        onClose={() => setEditField(null)}
        onSave={v => submitUpdate({ agent_type: v })}
      />
      <EditCheckboxDialog
        open={editField === 'admin'}
        title="Edit admin status"
        fieldLabel="Admin"
        checked={agent.admin}
        onClose={() => setEditField(null)}
        onSave={v => submitUpdate({ admin: v })}
      />
      <EditFieldDialog
        open={editField === 'email'}
        title="Edit email"
        fieldLabel="Email"
        value={agent.email || ''}
        onClose={() => setEditField(null)}
        onSave={v => submitUpdate({ email: v })}
        helperText="Email address for receiving notifications"
      />
      <EditPasswordDialog
        open={editField === 'password'}
        onClose={() => setEditField(null)}
        onSave={(pw, pwc) => submitUpdate({ password: pw, password_confirmation: pwc })}
        minimumLength={minimum_password_length}
      />
      <EditSelectDialog
        open={editField === 'agent_provider'}
        title="Edit provider"
        fieldLabel="Provider"
        value={agent.agent_provider || 'CLAUDE_CODE'}
        options={[{ value: 'CLAUDE_CODE', label: 'Claude Code' }]}
        onClose={() => setEditField(null)}
        onSave={v => submitUpdate({ agent_provider: v })}
      />
      <EditFieldDialog
        open={editField === 'agent_arguments'}
        title="Edit arguments"
        fieldLabel="Arguments"
        value={agent.agent_arguments || ''}
        onClose={() => setEditField(null)}
        onSave={v => submitUpdate({ agent_arguments: v })}
        multiline
        monospace
        helperText='JSON array of CLI arguments, e.g. ["--model", "sonnet"]'
      />
      <EditFieldDialog
        open={editField === 'profile'}
        title="Edit profile"
        fieldLabel="Profile"
        value={agent.profile || ''}
        onClose={() => setEditField(null)}
        onSave={v => submitUpdate({ profile: v })}
        multiline
        monospace
        helperText="System instructions for this agent (supports Markdown)"
      />
      <EditFieldDialog
        open={editField === 'memory'}
        title="Edit persistent memory"
        fieldLabel="Memory"
        value={agent.memory || ''}
        onClose={() => setEditField(null)}
        onSave={v => submitUpdate({ memory: v })}
        multiline
        monospace
        helperText="Agent's persistent memory (Markdown). Synced automatically by the agent runner."
      />
      <EditFieldDialog
        open={editField === 'calling'}
        title="Edit calling"
        fieldLabel="Calling"
        value={agent.calling || ''}
        onClose={() => setEditField(null)}
        onSave={v => submitUpdate({ calling: v })}
        multiline
        helperText="A persistent purpose prompt. When set, the agent will proactively act on this calling when idle."
      />
      <EditFieldDialog
        open={editField === 'calling_interval_minutes'}
        title="Edit calling interval"
        fieldLabel="Interval (minutes)"
        value={String(agent.calling_interval_minutes ?? 60)}
        onClose={() => setEditField(null)}
        onSave={v => submitUpdate({ calling_interval_minutes: parseInt(v) || 60 })}
        helperText="How often the agent acts on its calling (minimum 15 minutes, default 60)"
      />

      <ConfirmDialog
        open={!!revokeConnectionId}
        title="Revoke connection"
        message="Are you sure you want to revoke this connection? The agent will lose access immediately."
        confirmLabel="Revoke"
        onConfirm={handleRevokeConnection}
        onCancel={() => setRevokeConnectionId(null)}
      />

      <ConfirmDialog
        open={showDeleteConfirm}
        title="Delete agent"
        message={`Are you sure you want to delete agent '${agent.username}'? This cannot be undone.`}
        onConfirm={handleDelete}
        onCancel={() => setShowDeleteConfirm(false)}
      />
    </>
  )
}

// --- Memories Tab ---

const SUBJECT_TYPE_COLORS: Record<string, 'default' | 'primary' | 'secondary' | 'info' | 'success' | 'warning'> = {
  self: 'secondary',
  project: 'primary',
  task: 'info',
  agent: 'success',
  channel: 'warning',
}

const SUBJECT_TYPE_LABELS: Record<string, string> = {
  self: 'Self',
  project: 'Project',
  task: 'Task',
  agent: 'Agent',
  channel: 'Channel',
}

function MemoriesTab({ memories }: { memories: MemoryData[] }) {
  const [expandedId, setExpandedId] = useState<number | null>(null)
  const [filterType, setFilterType] = useState<string | null>(null)
  const [searchQuery, setSearchQuery] = useState('')

  const subjectTypes = useMemo(() => {
    const counts: Record<string, number> = {}
    for (const m of memories) {
      counts[m.subject_type] = (counts[m.subject_type] || 0) + 1
    }
    return Object.entries(counts).sort(([a], [b]) => a.localeCompare(b))
  }, [memories])

  const filteredMemories = useMemo(() => {
    let result = memories
    if (filterType) {
      result = result.filter(m => m.subject_type === filterType)
    }
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase()
      result = result.filter(m => m.content.toLowerCase().includes(q))
    }
    return result
  }, [memories, filterType, searchQuery])

  // Group by subject_type
  const grouped = useMemo(() => {
    const groups: Record<string, MemoryData[]> = {}
    for (const m of filteredMemories) {
      if (!groups[m.subject_type]) groups[m.subject_type] = []
      groups[m.subject_type].push(m)
    }
    return Object.entries(groups).sort(([a], [b]) => a.localeCompare(b))
  }, [filteredMemories])

  if (memories.length === 0) {
    return (
      <Paper elevation={0} sx={{ p: 4, border: '1px solid', borderColor: 'divider', borderRadius: 2, textAlign: 'center' }}>
        <MemoryIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
        <Typography variant="body1" color="text.secondary">
          No memories stored for this agent yet.
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          Memories are created by the agent during its work to remember context about projects, tasks, and other subjects.
        </Typography>
      </Paper>
    )
  }

  return (
    <Box>
      {/* Filter bar */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2, flexWrap: 'wrap' }}>
        <TextField
          size="small"
          placeholder="Search memories..."
          value={searchQuery}
          onChange={e => setSearchQuery(e.target.value)}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" color="action" />
                </InputAdornment>
              ),
            },
          }}
          sx={{ minWidth: 200, flexGrow: 1, maxWidth: 350 }}
        />
        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
          <Chip
            label={`All (${memories.length})`}
            size="small"
            variant={filterType === null ? 'filled' : 'outlined'}
            color={filterType === null ? 'primary' : 'default'}
            onClick={() => setFilterType(null)}
            sx={{ cursor: 'pointer' }}
          />
          {subjectTypes.map(([type, count]) => (
            <Chip
              key={type}
              label={`${SUBJECT_TYPE_LABELS[type] || type} (${count})`}
              size="small"
              variant={filterType === type ? 'filled' : 'outlined'}
              color={filterType === type ? (SUBJECT_TYPE_COLORS[type] || 'default') : 'default'}
              onClick={() => setFilterType(filterType === type ? null : type)}
              sx={{ cursor: 'pointer' }}
            />
          ))}
        </Box>
      </Box>

      {/* Results count */}
      {(filterType || searchQuery) && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          {filteredMemories.length} {filteredMemories.length === 1 ? 'memory' : 'memories'} found
        </Typography>
      )}

      {/* Grouped accordion */}
      {grouped.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
          No memories match your filters.
        </Typography>
      ) : (
        grouped.map(([type, typeMemories]) => (
          <Accordion
            key={type}
            defaultExpanded={grouped.length <= 3}
            disableGutters
            elevation={0}
            sx={{
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: '8px !important',
              mb: 1.5,
              '&:before': { display: 'none' },
              '&.Mui-expanded': { mb: 1.5 },
            }}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ minHeight: 48 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Chip
                  label={SUBJECT_TYPE_LABELS[type] || type}
                  size="small"
                  color={SUBJECT_TYPE_COLORS[type] || 'default'}
                />
                <Typography variant="body2" color="text.secondary">
                  {typeMemories.length} {typeMemories.length === 1 ? 'memory' : 'memories'}
                </Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails sx={{ pt: 0 }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {typeMemories.map(memory => (
                  <Paper
                    key={memory.id}
                    elevation={0}
                    sx={{
                      p: 2,
                      bgcolor: 'action.hover',
                      borderRadius: 1.5,
                      cursor: 'pointer',
                      '&:hover': { bgcolor: 'action.selected' },
                      transition: 'background-color 0.15s',
                    }}
                    onClick={() => setExpandedId(expandedId === memory.id ? null : memory.id)}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: expandedId === memory.id ? 1 : 0 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {memory.subject_id != null && (
                          <Typography variant="body2" fontWeight={500} color="text.secondary">
                            #{memory.subject_id}
                          </Typography>
                        )}
                        {expandedId !== memory.id && (
                          <Typography variant="body2" color="text.secondary" noWrap sx={{ maxWidth: 400 }}>
                            {memory.content.substring(0, 100)}{memory.content.length > 100 ? '...' : ''}
                          </Typography>
                        )}
                      </Box>
                      <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0, ml: 1 }}>
                        {new Date(memory.updated_at).toLocaleDateString()}
                      </Typography>
                    </Box>
                    {expandedId === memory.id && (
                      <Box sx={{ bgcolor: 'background.paper', p: 2, borderRadius: 1, maxHeight: 400, overflow: 'auto' }}>
                        <Markdown>{memory.content}</Markdown>
                      </Box>
                    )}
                  </Paper>
                ))}
              </Box>
            </AccordionDetails>
          </Accordion>
        ))
      )}
    </Box>
  )
}

// --- Main Component ---

export default function Edit({ agent, connections, memories, available_api_doc_skills, registration_url, installer_url, registration_token_expires_at, minimum_password_length }: Props) {
  const [activeTab, setActiveTab] = useState(0)

  function changeTab(newValue: number) {
    setActiveTab(newValue)
    const slug = TAB_SLUGS[newValue]
    const url = new URL(window.location.href)
    url.searchParams.set('tab', slug)
    window.history.pushState({}, '', url.toString())
  }

  // Initialize tab from URL
  React.useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    const tab = params.get('tab')
    if (tab) {
      const index = TAB_SLUGS.indexOf(tab as any)
      if (index >= 0) setActiveTab(index)
    }
  }, [])

  return (
    <SidebarLayout section="manage" title={`Agent: ${agent.username}`}>
      <Tabs
        value={activeTab}
        onChange={(_, v) => changeTab(v)}
        sx={{
          minHeight: 36,
          mb: 3,
          borderBottom: '1px solid',
          borderColor: 'divider',
          '& .MuiTab-root': { minHeight: 36, py: 0.5, textTransform: 'none', fontSize: '0.875rem' },
        }}
      >
        <Tab label="Settings" />
        <Tab label={`Memories (${memories.length})`} />
      </Tabs>

      {activeTab === 0 && (
        <SettingsTab
          agent={agent}
          connections={connections}
          available_api_doc_skills={available_api_doc_skills}
          registration_url={registration_url}
          installer_url={installer_url}
          registration_token_expires_at={registration_token_expires_at}
          minimum_password_length={minimum_password_length}
        />
      )}

      {activeTab === 1 && (
        <MemoriesTab memories={memories} />
      )}
    </SidebarLayout>
  )
}
